import { getUrlPath } from '@creator/ui/utils/url-utils';
import Icon from '@src/basic-components/Icon/Icon';
import IconAndLabel from '@src/basic-components/IconAndLabel/IconAndLabel';
import SocialShare, { type SocialShareProps } from '@src/components/SocialShare/SocialShare';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { getPostShareUrl } from '@src/model/upvote/helpers';
import { getUserId } from '@src/model/user/helpers';
import { usePost } from '@src/store/posts/hooks/usePost';
import { abbreviatedFormatter } from '@src/utils/number-utils/number-utils';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { type FC, memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import Reactions from '../Reactions/Reactions';

export type ShareUrlType = 'post' | 'response' | 'responseThread';

export interface PostStatsAndReactionsProps {
    className?: string;
    postId: string;
    tokenName: string;
    isDisabled?: boolean;
}

const PostStatsAndReactions: FC<PostStatsAndReactionsProps> = props => {
    const { tokenName, postId, className = '', isDisabled } = props;
    const post = usePost(postId);
    const navigate = useNavigate();
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);

    const onResponseClick = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isLoggedIn) return navigate(`/bbs/login?next=${getUrlPath()}`);
            navigate(`/bbs/${ensureTokenUrl(tokenName, false)}/posts/create?parentPostId=${postId}`);
        },
        [isLoggedIn, tokenName, postId, history]
    );

    const renderComments = () => {
        if (!post) return null;
        const count = abbreviatedFormatter(post.numComments);
        const label = count === '0' ? '' : count;

        return <IconAndLabel iconProps={{ className: 'text-lg' }} labelProps={{ className: 'text-sm' }} iconName="chat" label={label} isDisabled={isDisabled} />;
    };

    const renderResponse = () => {
        if (!post) return null;
        const count = post.totalChildPosts ? abbreviatedFormatter(post.totalChildPosts) : '0';
        const label = count === '0' ? '' : count;

        return (
            <IconAndLabel
                iconProps={{ className: 'text-lg' }}
                iconName="response"
                label={label}
                labelProps={{ className: 'text-sm' }}
                onClick={onResponseClick}
                isDisabled={isDisabled}
            />
        );
    };

    const renderShareIcon = () => {
        // Prepare props for SocialShare with the memoized callback
        const props: SocialShareProps = {
            shareUrlPayload: {
                url: getPostShareUrl(tokenName, postId),
            },
            useShortUrl: true,
            renderTrigger: () => <Icon className="text-lg" name="share" />,
        };

        return <SocialShare {...props} />;
    };

    return (
        <div className={`flex items-center ${className}`}>
            <Reactions className="" tokenName={tokenName} isDisabled={isDisabled} entityId={postId} entityType="Post" />
            {renderComments()}
            {renderResponse()}
            <div className="relative mr-1">{renderShareIcon()}</div>
        </div>
    );
};

export default memo(PostStatsAndReactions, propsEqualityFn);
